import { RefObject, useEffect } from "react";

type AnyEvent = MouseEvent | TouchEvent;

const IGNORED_ELEMENTS = [
  "rc-dropdown",
  "tooltip-portal__content",
  "project-menu-dropdown-popper__icon",
  "ws-button",
];

const hasIgnoredClass = (element: any): boolean => {
  if (element && element.classList) {
    return IGNORED_ELEMENTS.some((className) =>
      element.classList.contains(className),
    );
  }
  return false;
};

const isInIgnoredElement = (element: any): boolean => {
  if (!element) {
    return false;
  }

  if (hasIgnoredClass(element)) {
    return true;
  }

  if (element.parentElement) {
    return isInIgnoredElement(element.parentElement);
  }

  return false;
};

function useOnClickOutside<T extends HTMLElement = HTMLElement>(
  ref: RefObject<T>,
  handler: (event: AnyEvent) => void,
): void {
  useEffect(() => {
    const listener = (event: AnyEvent) => {
      const el = ref?.current;

      // Do nothing if clicking ref's element or descendent elements
      if (
        !el ||
        !el?.contains ||
        el.contains(event.target as Node) ||
        isInIgnoredElement(event.target)
      ) {
        return;
      }
      handler(event);
    };

    document.addEventListener(`mousedown`, listener);
    document.addEventListener(`touchstart`, listener);

    return () => {
      document.removeEventListener(`mousedown`, listener);
      document.removeEventListener(`touchstart`, listener);
    };

    // Reload only if ref or handler changes
  }, [ref, handler]);
}

export default useOnClickOutside;
